import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "modalTitle", "modalInfo",
    "actionSection", "actionSelect",
    "agentsSection", "agentsContainer",
    "scopesSection", "scopesContainer", "scopesEmpty",
    "ancestorTemplateId", "localOverrideId",
    "submitBtn", "removeBtn"
  ]

  static values = {
    permissionsUrl: String,
    parentInstances: Object,
    resourceActions: Object,
    actionLabels: Object,
    resourceScopes: Object
  }

  openRestriction(event) {
    event.preventDefault()
    this.populateModal(event.currentTarget.dataset)
    this.modalTarget.showModal()
  }

  populateModal(data) {
    const ancestorAction = data.ancestorAction
    const resourceType = data.resourceType
    const parentClass = data.parentClass || ''
    const overrideId = data.localOverrideId || ''
    const overrideAction = data.localOverrideAction || ancestorAction
    const overrideExclude = data.localOverrideExclude ? JSON.parse(data.localOverrideExclude) : []
    const overrideScopeKeys = data.localOverrideScopeKeys ? JSON.parse(data.localOverrideScopeKeys) : []
    const overrideScopeConfig = data.localOverrideScopeConfig ? JSON.parse(data.localOverrideScopeConfig) : {}

    this.currentAncestorAction = ancestorAction
    this.currentResourceType = resourceType

    this.ancestorTemplateIdTarget.value = data.ancestorTemplateId
    this.localOverrideIdTarget.value = overrideId

    const label = this.humanizeResourceType(resourceType)
    this.modalTitleTarget.textContent = overrideId ? 'Editar Restrição' : 'Restringir Permissão'
    this.modalInfoTarget.textContent = `${label} · Ação herdada: ${this.actionLabel(ancestorAction)}`

    this.setupActionSection(resourceType, ancestorAction, overrideAction)
    this.setupAgentsSection(parentClass, overrideExclude)
    this.setupScopesSection(resourceType, overrideScopeKeys, overrideScopeConfig)

    this.removeBtnTarget.classList.toggle('hidden', !overrideId)
  }

  setupActionSection(resourceType, ancestorAction, currentAction) {
    const select = this.actionSelectTarget
    select.innerHTML = ''

    if (ancestorAction !== 'manage') {
      this.actionSectionTarget.classList.add('hidden')
      return
    }

    this.actionSectionTarget.classList.remove('hidden')
    const actions = this.resourceActionsValue[resourceType] || ['manage']

    actions.forEach(action => {
      const opt = document.createElement('option')
      opt.value = action
      opt.textContent = this.actionLabel(action)
      opt.selected = action === currentAction
      select.appendChild(opt)
    })
  }

  setupAgentsSection(parentClass, excludedIds) {
    const container = this.agentsContainerTarget
    container.innerHTML = ''

    if (!parentClass || !this.parentInstancesValue[parentClass]?.length) {
      this.agentsSectionTarget.classList.add('hidden')
      return
    }

    this.agentsSectionTarget.classList.remove('hidden')
    const instances = this.parentInstancesValue[parentClass]

    instances.forEach(inst => {
      const label = document.createElement('label')
      label.className = 'flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors'

      const cb = document.createElement('input')
      cb.type = 'checkbox'
      cb.value = inst.id
      cb.checked = !excludedIds.includes(inst.id)
      cb.className = 'w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'

      const span = document.createElement('span')
      span.className = 'text-sm text-gray-700'
      span.textContent = inst.name

      label.appendChild(cb)
      label.appendChild(span)
      container.appendChild(label)
    })
  }

  // =========================================================================
  // Scope-based filter section
  // =========================================================================

  setupScopesSection(resourceType, activeScopeKeys, scopeConfig) {
    const container = this.scopesContainerTarget
    container.innerHTML = ''

    const scopes = this.resourceScopesValue[resourceType] || []

    if (scopes.length === 0) {
      this.scopesSectionTarget.classList.add('hidden')
      return
    }

    this.scopesSectionTarget.classList.remove('hidden')
    this.scopesEmptyTarget.classList.add('hidden')

    scopes.forEach(scope => {
      const isActive = activeScopeKeys.includes(scope.key)
      this.renderScopeToggle(container, scope, isActive, scopeConfig)
    })
  }

  renderScopeToggle(container, scope, isActive, scopeConfig) {
    const wrapper = document.createElement('div')
    wrapper.className = 'border border-gray-100 rounded-lg overflow-hidden'
    wrapper.dataset.scopeKey = scope.key

    // Toggle header
    const header = document.createElement('label')
    header.className = 'flex items-start gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors'

    const cb = document.createElement('input')
    cb.type = 'checkbox'
    cb.checked = isActive
    cb.className = 'scope-toggle mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
    cb.dataset.scopeKey = scope.key
    cb.addEventListener('change', () => this.onScopeToggle(wrapper, scope, cb.checked, scopeConfig))

    const textDiv = document.createElement('div')
    textDiv.className = 'flex-1 min-w-0'

    const nameSpan = document.createElement('span')
    nameSpan.className = 'text-sm font-medium text-gray-700 block'
    nameSpan.textContent = scope.label

    textDiv.appendChild(nameSpan)

    if (scope.description) {
      const desc = document.createElement('span')
      desc.className = 'text-xs text-gray-400 block mt-0.5'
      desc.textContent = scope.description
      textDiv.appendChild(desc)
    }

    header.appendChild(cb)
    header.appendChild(textDiv)
    wrapper.appendChild(header)

    // Options container (for configurable scopes)
    if (scope.configurable) {
      const optionsContainer = document.createElement('div')
      optionsContainer.className = 'scope-options px-3 pb-2.5 pl-9 space-y-1'
      optionsContainer.style.display = isActive ? '' : 'none'
      wrapper.appendChild(optionsContainer)

      if (isActive) {
        this.loadScopeOptions(scope, optionsContainer, scopeConfig)
      }
    }

    container.appendChild(wrapper)
  }

  onScopeToggle(wrapper, scope, checked, scopeConfig) {
    if (!scope.configurable) return

    const optionsContainer = wrapper.querySelector('.scope-options')
    if (!optionsContainer) return

    optionsContainer.style.display = checked ? '' : 'none'

    if (checked && optionsContainer.children.length === 0) {
      this.loadScopeOptions(scope, optionsContainer, scopeConfig)
    }
  }

  async loadScopeOptions(scope, container, scopeConfig) {
    if (scope.static_options) {
      // Pre-selected values for ui_only scopes
      const preselected = scopeConfig?.[scope.key] || []
      this.renderScopeOptions(container, scope.static_options, scope.key, preselected)
      return
    }

    if (scope.has_options_source) {
      container.innerHTML = '<span class="text-xs text-gray-400">Carregando opções...</span>'

      try {
        const url = new URL(this.permissionsUrlValue + '/configurable_scope_options', window.location.origin)
        url.searchParams.set('resource_type', this.currentResourceType)
        url.searchParams.set('scope_key', scope.key)

        const response = await fetch(url.toString(), {
          headers: { 'X-CSRF-Token': this.csrfToken, 'Accept': 'application/json' }
        })
        const options = await response.json()

        // Pre-selected values from filter config
        const preselected = this.extractPreselectedValues(scope.key, scopeConfig)

        container.innerHTML = ''
        if (options.length === 0) {
          container.innerHTML = '<span class="text-xs text-gray-400 italic">Nenhuma opção disponível.</span>'
        } else {
          this.renderScopeOptions(container, options, scope.key, preselected)
        }
      } catch (error) {
        console.error('Error loading scope options:', error)
        container.innerHTML = '<span class="text-xs text-red-400">Erro ao carregar opções.</span>'
      }
      return
    }
  }

  renderScopeOptions(container, options, scopeKey, preselectedValues) {
    container.innerHTML = ''

    options.forEach(opt => {
      const label = document.createElement('label')
      label.className = 'flex items-center gap-2 py-1 px-1 rounded hover:bg-gray-50 cursor-pointer transition-colors'

      const cb = document.createElement('input')
      cb.type = 'checkbox'
      cb.value = opt.id
      cb.checked = preselectedValues.map(String).includes(String(opt.id))
      cb.className = 'scope-option w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
      cb.dataset.scopeKey = scopeKey

      const span = document.createElement('span')
      span.className = 'text-xs text-gray-600'
      span.textContent = opt.label

      label.appendChild(cb)
      label.appendChild(span)
      container.appendChild(label)
    })
  }

  extractPreselectedValues(scopeKey, scopeConfig) {
    if (!scopeConfig) return []

    // ui_only scopes store directly: scope_config[scope_key] = ['val1', 'val2']
    const direct = scopeConfig[scopeKey]
    if (Array.isArray(direct)) return direct

    // filter-based scopes: look in scope_config.filters for matching column
    const filters = scopeConfig.filters || []
    for (const f of filters) {
      if (f.operator === 'in' && Array.isArray(f.value)) {
        return f.value
      }
    }

    return []
  }

  // =========================================================================
  // Data collection + submission
  // =========================================================================

  collectFormData() {
    const body = {
      ancestor_template_id: this.ancestorTemplateIdTarget.value,
      permission_action: null,
      parent_resolution_config: null,
      scope_keys: [],
      scope_values: {}
    }

    if (!this.actionSectionTarget.classList.contains('hidden')) {
      body.permission_action = this.actionSelectTarget.value
    }

    if (!this.agentsSectionTarget.classList.contains('hidden')) {
      const checkboxes = this.agentsContainerTarget.querySelectorAll('input[type="checkbox"]')
      const excluded = Array.from(checkboxes).filter(cb => !cb.checked).map(cb => parseInt(cb.value))
      body.parent_resolution_config = excluded.length > 0
        ? { all: true, exclude: excluded }
        : { all: true }
    }

    // Collect enabled scopes and their selected values
    const scopeToggles = this.scopesContainerTarget.querySelectorAll('.scope-toggle:checked')
    scopeToggles.forEach(toggle => {
      const scopeKey = toggle.dataset.scopeKey
      body.scope_keys.push(scopeKey)

      // Find the wrapper div (not the toggle itself) to collect option checkboxes
      const wrapper = this.scopesContainerTarget.querySelector(`div[data-scope-key="${scopeKey}"]`)
      if (!wrapper) return

      const optionCheckboxes = wrapper.querySelectorAll('.scope-option:checked')
      if (optionCheckboxes.length > 0) {
        body.scope_values[scopeKey] = Array.from(optionCheckboxes).map(cb => cb.value)
      }
    })

    return body
  }

  hasAnyRestriction(body) {
    const actionChanged = body.permission_action && body.permission_action !== this.currentAncestorAction
    const agentsExcluded = body.parent_resolution_config?.exclude?.length > 0
    const hasScopes = body.scope_keys.length > 0
    return actionChanged || agentsExcluded || hasScopes
  }

  async submitRestriction(event) {
    event.preventDefault()
    const body = this.collectFormData()

    if (!this.hasAnyRestriction(body)) {
      alert('Selecione pelo menos uma restrição para salvar.')
      return
    }

    const btn = this.submitBtnTarget
    const originalText = btn.textContent
    btn.disabled = true
    btn.textContent = 'Salvando...'

    try {
      const response = await fetch(`${this.permissionsUrlValue}/restrict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (data.status === 'success') {
        this.modalTarget.close()
        window.location.reload()
      } else {
        alert(data.errors?.join('\n') || 'Erro ao salvar restrição')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Erro ao salvar restrição')
    } finally {
      btn.disabled = false
      btn.textContent = originalText
    }
  }

  async removeRestriction(event) {
    event.preventDefault()
    if (!confirm('Remover esta restrição? A permissão voltará ao estado herdado.')) return

    const overrideId = this.localOverrideIdTarget.value
    if (!overrideId) return

    const btn = event.currentTarget
    btn.disabled = true

    try {
      const response = await fetch(`${this.permissionsUrlValue}/${overrideId}/unrestrict`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': this.csrfToken }
      })

      const data = await response.json()

      if (data.status === 'success') {
        this.modalTarget.close()
        window.location.reload()
      } else {
        alert(data.errors?.join('\n') || 'Erro ao remover restrição')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Erro ao remover restrição')
    } finally {
      btn.disabled = false
    }
  }

  async blockInheritance(event) {
    event.preventDefault()
    if (!confirm('Remover esta permissão do subgrupo? Membros perderão este acesso.')) return

    const btn = event.currentTarget
    const ancestorTemplateId = btn.dataset.ancestorTemplateId
    btn.disabled = true

    try {
      const response = await fetch(`${this.permissionsUrlValue}/block_inheritance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ ancestor_template_id: ancestorTemplateId })
      })

      const data = await response.json()

      if (data.status === 'success') {
        window.location.reload()
      } else {
        alert(data.errors?.join('\n') || 'Erro ao remover permissão')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Erro ao remover permissão')
    } finally {
      btn.disabled = false
    }
  }

  async reactivateInheritance(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const overrideId = btn.dataset.localOverrideId
    btn.disabled = true

    try {
      const response = await fetch(`${this.permissionsUrlValue}/${overrideId}/unrestrict`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': this.csrfToken }
      })

      const data = await response.json()

      if (data.status === 'success') {
        window.location.reload()
      } else {
        alert(data.errors?.join('\n') || 'Erro ao reativar herança')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Erro ao reativar herança')
    } finally {
      btn.disabled = false
    }
  }

  removeOwnPermission(event) {
    if (!confirm('Remover esta permissão do subgrupo? Os membros deixam de recebê-la.')) {
      event.preventDefault()
      return
    }

    this.reactivateInheritance(event)
  }

  closeModal(event) {
    event?.preventDefault()
    this.modalTarget.close()
  }

  get csrfToken() {
    return document.querySelector('[name="csrf-token"]')?.content || ''
  }

  actionLabel(action) {
    return this.actionLabelsValue[action] || action
  }

  humanizeResourceType(type) {
    return type.split('::').pop().replace(/([A-Z])/g, ' $1').trim()
  }
}
